import { useEffect, useState } from "react";

import { DateTime } from "luxon";

import {
  generateClientId,
  getRegionForCoordinates,
  getRoutePointLocationFromMapBoxFeature,
} from "utils";

import { RoutesForm } from "./RoutesForm";
import { areRouteTimesValid } from "../../utils";
import { addressIsEmpty } from "../RoutePoint/utils";

import type { SearchBoxFeatureSuggestion } from "@mapbox/search-js-core";

interface Props {
  onSubmit: (routePoint: Partial<RoutePoint>) => void;
}

export function RoutesFormContainer(props: Props) {
  const { onSubmit } = props;
  const [routePointState, setRoutePointState] =
    useState<EditableRoutePointState>({
      clientId: generateClientId(),
      stop_operation: "PICKUP",
      stop_type: "MID",
      start_time: DateTime.now().toFormat("yyyy-MM-dd"),
      isBeingEdited: false,
      inputMode: "autocomplete",
      schedule_type: "DATETIME",
      point_location: {
        address: "",
        city: "",
        province: "",
        country: "",
        postal: "",
        latitude: null,
        longitude: null,
        business_name: "",
      },
    });
  const [error, setError] = useState<string | null>(null);

  const onAddressSuggestionSelected = (feature: SearchBoxFeatureSuggestion) => {
    const location = getRoutePointLocationFromMapBoxFeature(feature);
    setRoutePointState((prev) => ({
      ...prev,
      point_location: location as RoutePointLocation,
    }));
  };

  const onAddressCleared = () => {
    setRoutePointState((prev) => ({
      ...prev,
      point_location: {} as RoutePointLocation,
    }));
  };

  const onRoutePointMetadataChange = (
    key: "start_time" | "stop_operation" | "schedule_type",
    value: string,
  ) => {
    setRoutePointState((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const updateInputMode = (mode: "autocomplete" | "manual") => {
    setRoutePointState((prev) => ({
      ...prev,
      inputMode: mode,
    }));
  };

  const validate = (): boolean => {
    const { start_time: startTime } = routePointState;
    if (addressIsEmpty(routePointState)) {
      setError("Address can not be empty.");
      return false;
    }
    if (areRouteTimesValid(startTime) != null) {
      setError(areRouteTimesValid(startTime));
      return false;
    }
    setError(null);
    return true;
  };

  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    onSubmit(routePointState);
  };

  const onUpdatePointLocation = <T,>(
    key: keyof RoutePointLocation,
    value: T,
  ) => {
    setRoutePointState((prev) => ({
      ...prev,
      point_location: {
        ...prev.point_location,
        [key]: value,
      } as RoutePointLocation,
    }));
  };

  /*
    Calls mapbox to find the region for the selected autocomplete location.
    This is required because most mapbox autocomplete locations
    come without province/region information
  */
  useEffect(() => {
    if (
      routePointState.point_location?.longitude &&
      routePointState.point_location?.latitude &&
      (routePointState.point_location.province === "" ||
        routePointState.point_location.province === null ||
        routePointState.point_location.province === undefined)
    ) {
      // call reverse geocode to get the province ?
      getRegionForCoordinates(
        routePointState.point_location.longitude,
        routePointState.point_location.latitude,
      ).then((region) => {
        if (region !== undefined) {
          onUpdatePointLocation("province", region);
        }
      });
    }
  }, [routePointState]);

  return (
    <RoutesForm
      routePoint={routePointState}
      onSubmit={handleSubmit}
      onRoutePointMetadataChange={onRoutePointMetadataChange}
      onAddressSuggestionSelected={onAddressSuggestionSelected}
      onAddressCleared={onAddressCleared}
      error={error}
      updateInputModeForRoutePoint={updateInputMode}
      onUpdatePointLocation={onUpdatePointLocation}
    />
  );
}
